import { DatePipe } from "@angular/common";
import { Component, computed, OnInit, signal, TemplateRef, ViewChild } from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { PageEvent } from "@angular/material/paginator";
import { ActivatedRoute, Router } from "@angular/router";
import { paginatorConfig } from "@app/app.const";
import { FilterBarComponent } from "@components/filters/filter-bar/filter-bar.component";
import { RefreshButtonComponent } from "@components/refresh-button/refresh-button.component";
import { DataTableComponent } from "@components/table/data-table/data-table.component";
import {
  TableAction,
  TableColumn,
  TableConfig,
  TableSortEvent,
} from "@components/table/models/table-config.model";
import { AppStateService } from "@services/index";
import { RequestService } from "@services/request/request.service";
import {
  FormAutocompleteComponent,
  FormAutocompleteOption,
} from "../../components/filters/form-autocomplete/form-autocomplete.component";
import { formatCountryCode } from "../../utils/country-code.util";
import { UserCartDialogComponent } from "./user-cart-dialog.component";

@Component({
  selector: "fh-user-list",
  templateUrl: "./user-list.component.html",
  styleUrls: ["./user-list.component.css"],
  standalone: true,
  imports: [
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatDialogModule,
    FilterBarComponent,
    DataTableComponent,
    FormAutocompleteComponent,
    ReactiveFormsModule,
    RefreshButtonComponent,
  ],
  providers: [DatePipe],
})
export class UserListComponent implements OnInit {
  /** Bound wrapper for the refresh button — see `getUserList()`. */
  refreshData = (): void => this.getUserList();

  // Template ref for Follow Up inline-edit cell (static:true → available before first render)
  @ViewChild("followUpTemplate", { static: true }) followUpTemplateRef!: TemplateRef<any>;
  @ViewChild("accessLevelTemplate", { static: true }) accessLevelTemplateRef!: TemplateRef<any>;

  // Inline edit state
  editingUserId = signal<string | null>(null);
  editValue = signal<string>("");
  changingAccessId = signal<string | null>(null);

  // Access-level promotion control — a deliberate change here grants full
  // active access immediately (no separate activation step), per the same
  // convention as the Access Approval page.
  readonly accessLevelOptions = [
    { value: "user", label: "User" },
    { value: "vendor", label: "Vendor" },
    { value: "admin", label: "Admin" },
    // Never emailed - by any email, including Email Outreach.
    { value: "blocked", label: "Blocked" },
  ];

  // State signals
  iso2 = signal<string | null>(null);
  access = signal<string>("user");
  /** "unsubscribed": only those unsubscribed from Email Outreach; null: anyone. */
  outreach = signal<string | null>(null);
  searchText = signal<string>("");
  pageIndex = signal<number>(0);
  pageSize = signal<number>(paginatorConfig.initialPageSize);
  sortProp = signal<string>("created_on");
  sortDir = signal<"asc" | "desc">("desc");
  isLoading = signal<boolean>(false);

  // Data signals
  users = signal<any[]>([]);
  total = signal<number>(0);

  // Computed
  destinationCountries = computed(() => this.appStateService.destinationCountries());

  countryOptions = computed<FormAutocompleteOption<string>[]>(() => {
    return this.destinationCountries().map((country) => ({
      value: country.iso2,
      label: country.name,
      count: country.itemCount,
    }));
  });
  countryFilterFormControl = new FormControl<string | null>(null);

  // Access-level filter (defaults to regular users).
  readonly accessOptions: FormAutocompleteOption<string>[] = [
    { value: "user", label: "User" },
    { value: "vendor", label: "Vendor" },
    { value: "admin", label: "Admin" },
    // Never emailed - by any email, including Email Outreach.
    { value: "blocked", label: "Blocked" },
  ];
  accessFilterFormControl = new FormControl<string>("user");

  // Email Outreach filter (the Email Outreach tab links here).
  readonly outreachOptions: FormAutocompleteOption<string>[] = [
    { value: "unsubscribed", label: "Unsubscribed from outreach" },
  ];
  outreachFilterFormControl = new FormControl<string | null>(null);

  tableConfig = computed<TableConfig<any>>(() => ({
    columns: this.getTableColumns(),
    data: this.users(),
    total: this.total(),
    loading: this.isLoading(),
    pageSizeOptions: paginatorConfig.pageSizeOptions,
    initialPageSize: paginatorConfig.initialPageSize,
    showFirstLastButtons: true,
    stickyHeader: true,
    highlightRowOnClick: false,
    rowClickable: false,
    detailsRoute: "/admin/user-details/:id",
    getDetailsRouteParams: (row) => ({ id: row.user_id }),
  }));

  tableActions = computed<TableAction<any>[]>(() => [
    // "visibility" is now used by fh-data-table's auto-rendered View Details link
    // (from config().detailsRoute) — shopping_cart avoids two identical eye icons
    // showing up in the same row for what are actually two different actions.
    {
      icon: "shopping_cart",
      label: "View Cart",
      handler: (row) => this.viewCart(row),
      color: "accent",
    },
  ]);

  readonly paginatorConfig = paginatorConfig;

  constructor(
    private auth: RequestService,
    private appStateService: AppStateService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private datePipe: DatePipe,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.searchText.set(params.search || "");
      this.pageSize.set(+(params.pageSize || paginatorConfig.initialPageSize));
      this.pageIndex.set(+(params.page || 0));
      this.iso2.set(params.iso2 || null);
      this.access.set(params.access || "user");
      this.outreach.set(params.outreach || null);
      this.sortDir.set((params.sortDir || "desc") as "asc" | "desc");
      this.sortProp.set(params.sortProp || "created_on");

      // for the first time when this page loads we need to patch value to form control
      this.countryFilterFormControl.patchValue(this.iso2() || null);
      this.accessFilterFormControl.patchValue(this.access() || "user");
      this.outreachFilterFormControl.patchValue(this.outreach());

      this.getUserList();
    });
  }

  onCountryChange(iso2: string | null): void {
    this.updateQueryParams({ iso2: iso2 || undefined, page: 0 });
  }

  onAccessChange(access: string | null): void {
    this.updateQueryParams({ access: access || "user", page: 0 });
  }

  onOutreachChange(outreach: string | null): void {
    this.updateQueryParams({ outreach: outreach || undefined, page: 0 });
  }

  onSearchChange(searchText: string): void {
    this.updateQueryParams({ search: searchText || undefined, page: 0 });
  }

  onResetFilters(): void {
    this.updateQueryParams({
      iso2: undefined,
      access: undefined,
      outreach: undefined,
      search: undefined,
      page: 0,
    });
  }

  onSortChange(event: TableSortEvent): void {
    this.updateQueryParams({
      sortProp: event.prop || undefined,
      sortDir: event.dir || undefined,
      page: 0,
    });
  }

  onPageChange(event: PageEvent): void {
    this.updateQueryParams({
      page: event.pageIndex,
      pageSize: event.pageSize,
    });
  }

  startEdit(row: any, event: Event): void {
    event.stopPropagation();
    this.editingUserId.set(row.user_id);
    this.editValue.set(row.follow_up ?? "");
    setTimeout(() => {
      (document.getElementById(`fup-${row.user_id}`) as HTMLInputElement)?.focus();
    }, 0);
  }

  saveFollowUp(row: any): void {
    if (this.editingUserId() !== row.user_id) return;
    const newValue = this.editValue();
    const oldValue = row.follow_up ?? "";
    this.editingUserId.set(null);
    if (newValue === oldValue) return;

    this.auth.patch("/api/v1/user", { userId: row.user_id, adminFollowup: newValue }).subscribe({
      next: (res: any) => {
        if (res.result_code === 1) {
          this.users.update((list) =>
            list.map((u) => (u.user_id === row.user_id ? { ...u, follow_up: newValue } : u))
          );
        }
      },
      error: (err) => console.error("Failed to update follow-up:", err),
    });
  }

  cancelEdit(): void {
    this.editingUserId.set(null);
  }

  isOwnAccount(row: any): boolean {
    return this.appStateService.currentUser$.value?.id === Number(row.user_id);
  }

  changeAccess(row: any, newAccess: string): void {
    if (!newAccess || newAccess === row.access || this.isOwnAccount(row)) return;

    this.changingAccessId.set(row.user_id);
    this.auth.patch("/api/v1/user", { userId: row.user_id, access: newAccess }).subscribe({
      next: (res: any) => {
        this.changingAccessId.set(null);
        if (res.result_code === 1) {
          // Promoting off "user" access (e.g. to vendor) also means this row
          // no longer matches the current list filter — just refetch so the
          // list stays consistent with what's actually in that access level.
          this.getUserList();
        }
      },
      error: (err) => {
        this.changingAccessId.set(null);
        console.error("Failed to change access level:", err);
      },
    });
  }

  viewCart(row: any): void {
    this.dialog.open(UserCartDialogComponent, {
      data: {
        userId: row.user_id,
        userName: row.full_name?.trim() || row.email || `User #${row.user_id}`,
      },
      width: "860px",
      maxWidth: "95vw",
      maxHeight: "90vh",
    });
  }

  private getUserList(): void {
    const apiUrl = "/api/v1/user_list/search";
    const payload = {
      start: this.pageIndex() * this.pageSize(),
      end: (this.pageIndex() + 1) * this.pageSize(),
      iso2: this.iso2() || "",
      access: this.access() || "user",
      outreach: this.outreach() || undefined,
      search: this.searchText(),
      sortDir: this.sortDir(),
      sortProp: this.sortProp(),
    };

    this.isLoading.set(true);
    this.auth.post(apiUrl, payload).subscribe({
      next: (res: any) => {
        if (res.result_code === 1) {
          this.total.set(res.data.totalCount || 0);
          this.users.set(res.data?.list || []);
        }
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error("Error fetching users:", error);
        this.users.set([]);
        this.total.set(0);
        this.isLoading.set(false);
      },
    });
  }

  private updateQueryParams(params: any): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: params,
      queryParamsHandling: "merge",
    });
  }

  private getTableColumns(): TableColumn<any>[] {
    return [
      {
        key: "user_id",
        header: "ID",
        sortable: true,
        cssClass: "wrap-cell",
      },
      {
        key: "full_name",
        header: "Name",
        sortable: true,
      },
      {
        key: "email",
        header: "Contact",
        sortable: true,
        cssClass: "wrap-cell",
        // Was two separate columns (Email, Phone) — combined the same way as
        // In Cart, one glance shows both.
        format: (value, row) => {
          const email = value || "—";
          const phone = row.phone_number
            ? `${row.phone_country_code || ""}${row.phone_number}`
            : "—";
          // Unsubscribed from Email Outreach (the user's page can re-subscribe them).
          const outreach = row.outreach_unsubscribed ? " · Unsubscribed from outreach" : "";
          return `${email} / ${phone}${outreach}`;
        },
      },
      {
        key: "created_on",
        header: "Registered On",
        sortable: true,
        type: "date",
        // Show date + time in Singapore time (SGT, UTC+8).
        format: (value) => {
          const t = this.datePipe.transform(value, "dd MMM yy, h:mm a", "+0800");
          return t ? `${t} SGT` : "—";
        },
      },
      {
        key: "last_access",
        header: "Last Access",
        sortable: true,
        type: "date",
        format: (value) => {
          if (!value || value === "2000-01-01T11:43:26.525Z") return "—";
          const t = this.datePipe.transform(value, "dd MMM yy, h:mm a", "+0800");
          return t ? `${t} SGT` : "—";
        },
      },
      {
        key: "no_of_items",
        header: "In Cart",
        sortable: true,
        cssClass: "wrap-cell",
        // Was two separate columns (In Cart, Cart Value) — combined so one glance
        // shows both the item count and what it's worth.
        format: (value, row) =>
          `${Number(value || 0)} items / $${Number(row.cart_value || 0).toFixed(2)}`,
      },
      {
        key: "order_amount",
        header: "Order Total",
        sortable: true,
        type: "number",
        format: (value) => `$${Number(value || 0).toFixed(2)}`,
      },
      {
        key: "iso2",
        header: "Country",
        sortable: true,
        format: formatCountryCode,
      },
      {
        key: "follow_up",
        header: "Follow Up",
        sortable: false,
        cssClass: "wrap-cell",
        template: this.followUpTemplateRef,
      },
      {
        key: "remark",
        header: "Remarks",
        sortable: false,
        cssClass: "wrap-cell",
        // No longer sliced to 25 chars — the column now wraps instead of
        // truncating, so the full remark can actually be read.
        format: (value) => value || "—",
      },
      {
        key: "access",
        header: "Access Level",
        sortable: false,
        // Width set via .mat-column-access in the CSS (table-layout: fixed
        // needs the width there, not as an inline style, to be authoritative) —
        // just enough for the select's longest option ("Vendor") plus its arrow.
        template: this.accessLevelTemplateRef,
      },
    ];
  }
}
